import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { JIRA_SITE_URL } from '@elementary/common-ui/constants/queryKeys';
import { Alert, Dialog, Loading } from '@elementary/common-ui/components';
import useNango from 'shared/hooks/useNango';
import * as api from 'shared/services/settingsApi';
import JiraServiceAccountContent from './JiraServiceAccountContent';

type Props = {
  open: boolean;
  onClose: () => void;
  environmentId: string;
  integrationId: string;
};

const JiraReauthDialog = ({
  open,
  onClose,
  environmentId,
  integrationId,
}: Props) => {
  const { nangoAuth, success: nangoSuccess, error: nangoError } = useNango();

  const { data: siteUrlData, isLoading: siteUrlLoading } = useQuery(
    [JIRA_SITE_URL, environmentId, integrationId],
    () => api.fetchJiraSiteUrl(environmentId, integrationId),
    { enabled: open && !!integrationId },
  );

  const atlasDomain = siteUrlData?.base_url
    ? siteUrlData.base_url.replace(/^https?:\/\//, '')
    : undefined;

  useEffect(() => {
    if (nangoSuccess) {
      onClose();
    }
  }, [nangoSuccess, onClose]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      customWidth={800}
      title="Re-authenticate Jira"
      confirmText={nangoError ? 'Try again' : 'Reconnect with Jira'}
      cancelText="Cancel"
      onConfirmButtonClick={() =>
        nangoAuth('jira-v2', { prompt: 'login' }, undefined, environmentId)
      }
      isLoading={siteUrlLoading}
      confirmDisabled={siteUrlLoading}
      message={
        siteUrlLoading ? (
          <Loading />
        ) : nangoError ? (
          <Alert severity="error" style={{ fontWeight: 'bold' }}>
            Jira re-authentication failed. Please try again or contact support.
          </Alert>
        ) : (
          <JiraServiceAccountContent
            ctaLabel="Reconnect with Jira"
            atlasDomain={atlasDomain}
          />
        )
      }
    />
  );
};

export default JiraReauthDialog;
